import React, { useMemo, useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Tooltip,
  Box,
  Grid,
  FormHelperText,
  RadioGroup,
  Radio,
  Collapse,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { fieldStyles } from "services/utils";
import CalculateIcon from "@mui/icons-material/Calculate";
import { useNavigate } from "react-router-dom";
import SnackbarWrapper from "components/Snackbar";
import AddIcon from "@mui/icons-material/Add";
import RevenueGeneratorFields from "components/RevGeneratorFields";
import {
  addRevenueGenerator,
  updateRevenueGenerator,
  deleteRevenueGenerator,
  addRevenueGeneratorSim,
  updateRevenueGeneratorSim,
  deleteRevenueGeneratorSim,
  deleteRevenueGeneratorsByJobId,
  deleteRevenueGeneratorsSimulationByJobId,
  getRequirementDetails,
  getScenarioJobDetails,
} from "services/api";

const ManpowerReqDialog = ({
  open,
  onClose,
  manpowerReq,
  onSave,
  onChangeRev,
  jobTitle,
  job,
  allJobs,
  isScenario,
  loc,
  scenario,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [revLoading, setRevLoading] = useState(false);
  const [revUpdateLoading, setRevUpdateLoading] = useState(false);
  const [reqLoading, setReqLoading] = useState(false);
  console.log("Job::", job);
  console.log("All Jobs::", allJobs);

  //array has backend data: avgProductivityPerMonth, companyTargetsPerYear, id, jobId, name for matching generator(s)
  //matching revenue generators are rev.generators for the selected job
  const [matchingRevGenerators, setMatchingRevGenerators] = useState([]);
  const [productivityInputs, setProductivityInputs] = useState({});
  const [employeeWorkloads, setEmployeeWorkloads] = useState([]);

  const initialRequirementState = {
    requirementId: null,
    jobId: null,
    ratio: "",
    contractualHoursPerWeek: "",
    actualHoursPerWeek: "",
    revenueGenerator: false,
    alternativeRatioName: null,
    alternativeRatioValue: "",
    ratioName: null,
    ratioQuantity: "",
    alternativeRatioQuantity: "",
    dailyUtilizationHrs: "",
    weeklyUtilizationDays: "",
    dailyEmpWorkingHrs: "",
    empWorkingDaysPerWeek: "",
    vacationDays: "",
    minimumFTE: "",
    altDailyUtilizationHrs: "",
    altWeeklyUtilizationDays: "",
    altDailyEmpWorkingHrs: "",
    altEmpWorkingDaysPerWeek: "",
    altVacationDays: "",
    isShifts: null,
    isMultipleRatios: null,
  };

  const [formData, setFormData] = React.useState({
    requirement: initialRequirementState,
    revenueRecords: [], // Initialize as an empty array
  });

  const [ratioYes, setRatioYes] = useState("");
  const [ratioNo, setRatioNo] = useState("");
  const [hasEmployeeRatio, setHasEmployeeRatio] = useState(""); // no radio button is selected

  const snackbarRef = useRef();
  const navigate = useNavigate();
  const [showMore, setShowMore] = useState(false);
  const [altShowMore, setAltShowMore] = useState(false);

  const productivityOptions = useMemo((e) => {
    const prod = [
      { label: t("admin.manpowerRequirement.productivityOptions.month"), value: "month" },
      { label: t("admin.manpowerRequirement.productivityOptions.quarter"), value: "quarter" },
      { label: t("admin.manpowerRequirement.productivityOptions.year"), value: "year" },
    ];
    return prod;
  }, []);

  //
  const handleChangeRev = (id, field, value) => {
    setMatchingRevGenerators((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  //When pressing add entry button
  const handleAddRevenueGenerator = () => {
    //  Create a unique id for the new row (if you don't have backend id yet)
    const newId = Date.now(); // or use a counter

    setMatchingRevGenerators((prev) => [
      ...prev,
      {
        id: newId, // <-- required to track individually
        isNew: true,
        jobId: job.jobId,
        companyTargetsPerYear: "",
        avgProductivityPerMonth: "",
      },
    ]);

    // 3️⃣ Add to UI-only state for raw input & period
    setProductivityInputs((prev) => ({
      ...prev,
      [newId]: {
        raw: "", // user will type this
        period: "month", // default
      },
    }));
  };

  //Saving Rev Generator
  const handleSaveFromChild = async (data) => {
    setRevLoading(true);
    setLoading(true);

    const payload = {
      jobId: !isScenario ? data.jobId : job.id,
      name: data.name || "",
      companyTargetsPerYear: data.companyTargetsPerYear,
      avgProductivityPerMonth: data.avgProductivityPerMonth,
    };

    // const result = await addRevenueGenerator(payload);
    let result;
    if (!isScenario) {
      // Normal revenue generator
      result = await addRevenueGenerator(payload);
    } else {
      // Scenario simulation
      result = await addRevenueGeneratorSim(payload);
    }

    if (result === "error") {
      snackbarRef.current?.showSnackbar(t("admin.manpowerRequirement.failedToSave"), "error");
      setRevLoading(false);
      setLoading(false);

      return;
    }

    // adding the backend id for the record and making for it an old flag
    setMatchingRevGenerators((prev) =>
      prev.map((item) =>
        item.id === data.id ? { ...item, id: result.id ?? item.id, isNew: false } : item
      )
    );

    setProductivityInputs((prev) => {
      const updated = { ...prev };
      // remove the old temporary key if ID changed
      if (result.id !== data.id) {
        delete updated[data.id];
      }
      // store new ID data, in order to be shown in UI
      updated[result.id] = {
        raw: result.avgProductivityPerMonth || "",
        period: "month",
      };
      setRevLoading(false);
      setLoading(false);

      return updated;
    });

    if (!manpowerReq || manpowerReq.revenueGenerator === false) {
      let updatedRequirementData = { ...formData.requirement };

      updatedRequirementData = {
        ...updatedRequirementData,
        revenueGenerator: true,
        ratio: null,
        ratioName: null,
        ratioQuantity: "",
        alternativeRatioName: null,
        alternativeRatioQuantity: "",
        alternativeRatioValue: "",
        dailyUtilizationHrs: "",
        weeklyUtilizationDays: "",
        dailyEmpWorkingHrs: "",
        empWorkingDaysPerWeek: "",
        vacationDays: "",
        altDailyUtilizationHrs: "",
        altWeeklyUtilizationDays: "",
        altDailyEmpWorkingHrs: "",
        altEmpWorkingDaysPerWeek: "",
        altVacationDays: "",
        isShifts: null,
        isMultipleRatios: null,
      };

      await onSave(updatedRequirementData);

      // onSave(updatedFormData);
      setLoading(false);
    } else {
      setLoading(false);
    }

    // await onChangeRev();
    setRevLoading(false);

    snackbarRef.current?.showSnackbar(t("user.functional.achievementSaved"), "success");
  };

  //Updating Rev Generator
  const handleUpdateFromChild = async (formData) => {
    try {
      setRevUpdateLoading(true);
      // 🧩 Find the existing record
      const existing = matchingRevGenerators.find((item) => item.id === formData.id);
      if (!existing) {
        snackbarRef.current?.showSnackbar(t("admin.manpowerRequirement.notFound"), "error");
        return;
      }

      // 🧠 Merge old + new values to ensure all fields are preserved
      const payload = {
        jobId: formData.jobId ?? existing.jobId,
        name: formData.name ?? existing.name,
        companyTargetsPerYear: formData.companyTargetsPerYear ?? existing.companyTargetsPerYear,
        avgProductivityPerMonth:
          formData.avgProductivityPerMonth ?? existing.avgProductivityPerMonth,
      };

      // const result = await updateRevenueGenerator(formData.id, payload);
      let result;
      if (!isScenario) {
        // Normal revenue generator
        result = await updateRevenueGenerator(formData.id, payload);
      } else {
        // Scenario simulation
        result = await updateRevenueGeneratorSim(formData.id, payload);
      }

      if (result === "error") {
        snackbarRef.current?.showSnackbar(t("admin.manpowerRequirement.failedToUpdate"), "error");
        return;
      }

      setMatchingRevGenerators((prev) =>
        prev.map((item) => (item.id === formData.id ? { ...item, ...result } : item))
      );

      setProductivityInputs((prev) => ({
        ...prev,
        [formData.id]: {
          raw: result.avgProductivityPerMonth ?? prev[formData.id]?.raw ?? "",
          period: "month",
        },
      }));

      // await onChangeRev("update", matchingRevGenerators);
      // await onChangeRev();
      snackbarRef.current?.showSnackbar(t("user.functional.achievementSaved"), "success");
    } catch (error) {
      console.error("Error updating revenue generator:", error);
      snackbarRef.current?.showSnackbar(t("admin.manpowerRequirement.unexpectedError"), "error");
    } finally {
      setRevUpdateLoading(false);
    }
  };

  //Deleting Revenue Generator
  const handleDeleteRevenueGenerator = async (id) => {
    try {
      // const result = await deleteRevenueGenerator(id); // your API helper
      let result;
      if (!isScenario) {
        // Normal revenue generator
        result = await deleteRevenueGenerator(id);
      } else {
        // Scenario simulation
        result = await deleteRevenueGeneratorSim(id);
      }

      if (result === "error") {
        snackbarRef.current?.showSnackbar(t("admin.manpowerRequirement.failedToDelete"), "error");
        return;
      }

      // Remove the deleted item from local arrays/objects
      setMatchingRevGenerators((prev) => prev.filter((item) => item.id !== id));

      setProductivityInputs((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });

      // await onChangeRev("delete", [id]);
      // await onChangeRev();

      snackbarRef.current?.showSnackbar(t("user.functional.achievementSaved"), "success");
    } catch (err) {
      console.error("Delete failed:", err);
      snackbarRef.current?.showSnackbar(t("user.functional.failedToUpdate"), "error");
    }
  };

  // handle raw and period changes
  const handleProductivityChange = (id, raw, period) => {
    setProductivityInputs((prev) => ({
      ...prev,
      [id]: { raw, period },
    }));

    const num = parseFloat(raw) || 0;
    let monthly = 0;

    switch (period) {
      case "month":
        monthly = num;
        break;
      case "quarter":
        monthly = num / 3;
        break;
      case "semi":
        monthly = num / 6;
        break;
      case "year":
        monthly = num / 12;
        break;
      default:
        monthly = num;
    }

    setMatchingRevGenerators((prev) =>
      prev.map((item) => (item.id === id ? { ...item, avgProductivityPerMonth: monthly } : item))
    );
  };

  React.useEffect(() => {
    const fetchDetails = async () => {
      setReqLoading(true);
      //Job already has a requirement

      try {
        if (manpowerReq && (manpowerReq.requirementId || manpowerReq.id) && open) {
          // --- Case 1: Fetch and Set ---
          //get requirement details of job
          let reqDetails;
          if (isScenario) {
            // Call scenario job details endpoint
            reqDetails = await getScenarioJobDetails(manpowerReq.id);
          } else {
            // Call regular requirement details endpoint
            reqDetails = await getRequirementDetails(manpowerReq.requirementId);
            console.log("Req Details::", reqDetails);
          }

          if (reqDetails && reqDetails !== "error") {
            setFormData(reqDetails); //update form data

            // Access the requirement
            const req = reqDetails.requirement;
            const isEmpRatio = req.ratioName === "x*;_q*n!!e&m%p"; // Does it have ratio to emp?

            setHasEmployeeRatio(isEmpRatio ? "yes" : "no"); //If ratio to emp: yes, else no
            setRatioYes(isEmpRatio ? (req.ratio ?? "") : ""); // if ratio to emp put it in ratioYes
            setRatioNo(!isEmpRatio ? (req.ratio ?? "") : ""); // if not ratio to emp put it in ratioNo

            //For rev generators:
            const records = reqDetails.revenueRecords || [];

            setMatchingRevGenerators(
              records.map((item) => ({
                ...item, // Keep all the DB data (id, name, etc.)
                isNew: false, // 👈 YOU MANUALLY ADD THIS HERE
              }))
            );
            const initialInputs = {};
            records.forEach((item) => {
              initialInputs[item.id] = {
                raw: item.avgProductivityPerMonth || "", // Map DB value to UI "raw" field
                period: "month", // Default period
              };
            });
            setProductivityInputs(initialInputs);
            const workloads = reqDetails.employeeWorkloads || [];
            setEmployeeWorkloads(workloads);
          }
        } else {
          // --- Case 2: Reset ---
          setFormData({
            requirement: initialRequirementState,
            revenueRecords: [],
          });
          setHasEmployeeRatio(""); // Set a default for new jobs
          setRatioYes("");
          setRatioNo("");
        }
      } catch (err) {
        console.error("Failed to load requirement details", err);
      } finally {
        setReqLoading(false); // 🔵 STOP LOADING (always)
      }
    };

    fetchDetails();
  }, [manpowerReq, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setFormData((prev) => ({
      ...prev, // 1. Keep the revenueRecords array intact
      requirement: {
        ...prev.requirement, // 2. Keep all other fields in the requirement object
        [field]: value, // 3. Update only the specific field (e.g., actualHoursPerWeek)
      },
    }));
  };

  const handleAddExtraRatio = () => {
    setFormData((prev) => ({
      ...prev, // 1. Keep the revenueRecords as they are
      requirement: {
        ...prev.requirement, // 2. Keep the existing requirement data
        isMultipleRatios: true, // 3. Update this specific field
        // 4. Update shifts, ensuring it's not null
        isShifts: prev.requirement.isShifts ?? false,
      },
    }));
  };

  const handleClose = (event, reason) => {
    if (event) event.stopPropagation(); // 1. Stop click from bubbling
    // 2. If user clicks backdrop or hits Escape
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      setFormData({
        requirement: initialRequirementState,
        revenueRecords: [],
      });
      setRatioYes("");
      setRatioNo("");
      setHasEmployeeRatio("");
      setMatchingRevGenerators([]);
      setProductivityInputs({});

      setShowMore(false);
      setAltShowMore(false);

      onClose();
    }
  };

  // Saving Manpower Requirement
  const handleSave = async () => {
    setLoading(true);
    try {
      // 1. Extract the requirement object from form data to keep the payload flat
      let updatedFormData = { ...formData.requirement };

      if (updatedFormData.revenueGenerator) {
        // Case 1: Revenue generator (Clean up all ratio/utilization fields)
        updatedFormData = {
          ...updatedFormData,
          revenueGenerator: true,
          ratio: null,
          ratioName: null,
          ratioQuantity: null,
          dailyUtilizationHrs: null,
          weeklyUtilizationDays: null,
          dailyEmpWorkingHrs: null,
          empWorkingDaysPerWeek: null,
          alternativeRatioName: null,
          alternativeRatioQuantity: null,
          alternativeRatioValue: null,
          vacationDays: null,
          altDailyUtilizationHrs: null,
          altWeeklyUtilizationDays: null,
          altDailyEmpWorkingHrs: null,
          altEmpWorkingDaysPerWeek: null,
          altVacationDays: null,
          isShifts: null,
          isMultipleRatios: null,
        };
      } else if (hasEmployeeRatio === "yes") {
        // Case 2: Non-revenue generator + Employee ratio ("Magic String")
        updatedFormData = {
          ...updatedFormData,
          revenueGenerator: false,
          ratioQuantity: null,
          ratio: ratioYes,
          ratioName: "x*;_q*n!!e&m%p",
          dailyUtilizationHrs: null,
          weeklyUtilizationDays: null,
          dailyEmpWorkingHrs: null,
          empWorkingDaysPerWeek: null,
          vacationDays: null,
          alternativeRatioName: null,
          alternativeRatioQuantity: null,
          alternativeRatioValue: null,
          altDailyUtilizationHrs: null,
          altWeeklyUtilizationDays: null,
          altDailyEmpWorkingHrs: null,
          altEmpWorkingDaysPerWeek: null,
          altVacationDays: null,
          isShifts: null,
          isMultipleRatios: null,
        };

        // Handle DB cleanup for revenue generators
        if (!isScenario) {
          await deleteRevenueGeneratorsByJobId(job.jobId);
        } else {
          await deleteRevenueGeneratorsSimulationByJobId(job.id);
        }
      } else {
        updatedFormData = {
          ...updatedFormData,
          revenueGenerator: false,
          ratio: ratioNo,
          // Reset name to null if it was previously the employee ratio string
          ratioName:
            updatedFormData.ratioName === "x*;_q*n!!e&m%p" ? null : updatedFormData.ratioName,
        };

        if (!isScenario) {
          await deleteRevenueGeneratorsByJobId(job.jobId);
        } else {
          await deleteRevenueGeneratorsSimulationByJobId(job.id);
        }
      }

      // 2. Pass ONLY the requirement object to onSave (Keeping payload flat)
      const saveResult = await onSave(updatedFormData, false);

      if (saveResult) {
        setLoading(false);
        onClose();
      }
    } catch (err) {
      setLoading(false);
      snackbarRef.current?.showSnackbar(t("admin.manpowerRequirement.saveFailed"), "error");
    }
  };

  const hasPendingNew = matchingRevGenerators.some((item) => item.isNew);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        onClick: (e) => e.stopPropagation(),
      }}
    >
      {reqLoading ? (
        <Box minHeight={250} display="flex" alignItems="center" justifyContent="center">
          <CircularProgress />
        </Box>
      ) : (
        <>
          <DialogTitle>
            <Typography variant="h5" noWrap>
              {manpowerReq
                ? t("admin.manpowerRequirement.updateTitle")
                : t("admin.manpowerRequirement.addTitle")}
              {" " + t("admin.manpowerRequirement.for") + " "}
              <Tooltip title={jobTitle || ""} arrow>
                <span
                  style={{
                    maxWidth: 200,
                    display: "inline-block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    verticalAlign: "bottom",
                  }}
                >
                  {jobTitle?.length > 20 ? jobTitle.slice(0, 20) + "..." : jobTitle}
                </span>
              </Tooltip>
            </Typography>
          </DialogTitle>

          <DialogContent dividers>
            {/* --- Part 1: Revenue Generator --- */}
            <FormControl fullWidth margin="dense" size="small">
              <InputLabel id="revenue-generator-label">
                {t("admin.manpowerRequirement.revenueType")}
              </InputLabel>
              <Select
                labelId="revenue-generator-label"
                value={formData.requirement?.revenueGenerator ? "revenue" : "non-revenue"}
                // onChange={(e) =>
                //   setFormData((prev) => ({
                //     ...prev,
                //     revenueGenerator: e.target.value === "revenue",
                //   }))
                // }
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev, // 1. Keep top level (revenueRecords)
                    requirement: {
                      ...prev.requirement, // 2. Keep all other requirement fields (jobId, ratio, etc.)
                      revenueGenerator: e.target.value === "revenue", // 3. Update only this field
                    },
                  }))
                }
                sx={fieldStyles}
              >
                <MenuItem value="revenue">
                  {t("admin.manpowerRequirement.revenueGenerator")}
                </MenuItem>
                <MenuItem value="non-revenue">
                  {t("admin.manpowerRequirement.nonRevenueGenerator")}
                </MenuItem>
              </Select>
            </FormControl>

            <TextField
              label={t("admin.manpowerRequirement.minimumFTERequirement")}
              value={formData.requirement?.minimumFTE ?? ""}
              onChange={(e) => {
                const value = e.target.value;
                // allow only empty or single digit 0–9
                if (value === "" || /^[0-9]+$/.test(value)) {
                  // setFormData((prev) => ({ ...prev, minimumFTE: value }));
                  setFormData((prev) => ({
                    ...prev, // Keep revenueRecords
                    requirement: {
                      ...prev.requirement, // Keep other requirement fields (jobId, ratio, etc.)
                      minimumFTE: value, // Update only this field
                    },
                  }));
                }
              }}
              sx={{ mt: 2, width: "200px" }} // 👈 smaller width
            />

            {/* --- Part 2: Ratio OR Targets & Productivity --- */}
            {!formData.requirement?.revenueGenerator ? (
              <Box mt={2}>
                <Typography variant="caption" color="textSecondary">
                  {t("admin.manpowerRequirement.ratioInfo")}
                </Typography>

                <Box display="flex" alignItems="center" gap={2} mt={1}>
                  <Typography variant="body2">
                    {/* {manpowerReq ? "Ratio To Employees:" : "Does this job have ratio to employee"} */}
                    {manpowerReq
                      ? t("admin.manpowerRequirement.ratioToEmp")
                      : t("admin.manpowerRequirement.ratioToEmployeesTooltip")}
                  </Typography>

                  <FormControl>
                    <RadioGroup
                      row
                      value={hasEmployeeRatio} // <-- new state for this question
                      onChange={(e) => setHasEmployeeRatio(e.target.value)} // <-- handle change
                    >
                      <FormControlLabel
                        value="yes"
                        control={
                          <Radio
                            size="small"
                            icon={
                              <span
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  border: "2px solid #1976d2",
                                  display: "inline-block",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  border: "2px solid #1976d2",
                                  display: "inline-block",
                                  backgroundColor: "#1976d2",
                                }}
                              />
                            }
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {t("admin.manpowerRequirement.ratioYes")}
                          </Typography>
                        }
                      />

                      <FormControlLabel
                        value="no"
                        control={
                          <Radio
                            size="small"
                            icon={
                              <span
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  border: "2px solid #1976d2",
                                  display: "inline-block",
                                }}
                              />
                            }
                            checkedIcon={
                              <span
                                style={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: "50%",
                                  border: "2px solid #1976d2",
                                  display: "inline-block",
                                  backgroundColor: "#1976d2",
                                }}
                              />
                            }
                          />
                        }
                        label={
                          <Typography variant="caption">
                            {t("admin.manpowerRequirement.ratioNo")}
                          </Typography>
                        }
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {hasEmployeeRatio === "yes" && (
                  <Box display="flex" flexDirection="column" alignItems="flex-start" mt={1}>
                    {/* 💡 Hint Message */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 0, fontStyle: "italic" }}
                    >
                      💡{" "}
                      {t("admin.manpowerRequirement.ratioHint", {
                        defaultValue:
                          "Hint: If your data is automated, the ratios will usually be smaller.",
                      })}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mt={1}>
                      <Typography variant="body1">
                        {t("admin.manpowerRequirement.ratioLabel")} 1 :
                      </Typography>
                      <TextField
                        label={t("admin.manpowerRequirement.value")}
                        type="number"
                        margin="dense"
                        size="small"
                        // value={formData?.ratio || ""}
                        value={ratioYes}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          if (value > 0) {
                            setRatioYes(value);
                          }
                        }}
                        inputProps={{ min: 0, style: { width: "100px" } }}
                      />
                    </Box>

                    <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                      {t("admin.manpowerRequirement.empManaged")}
                    </Typography>
                  </Box>
                )}

                {hasEmployeeRatio === "no" && (
                  <Box display="flex" flexDirection="column" gap={1} mt={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        label={t("admin.manpowerRequirement.name")}
                        margin="dense"
                        size="small"
                        value={
                          formData.requirement?.ratioName === "x*;_q*n!!e&m%p"
                            ? ""
                            : formData.requirement?.ratioName || ""
                        }
                        onChange={(e) => handleChange("ratioName")(e)}
                        inputProps={{ min: 0 }}
                        sx={{ width: "200px" }}
                      />

                      <Typography variant="caption" color="textSecondary">
                        {t("admin.manpowerRequirement.unitNameTooltip")}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        label={t("admin.manpowerRequirement.quantity")}
                        type="number"
                        margin="dense"
                        size="small"
                        value={formData.requirement?.ratioQuantity || ""}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          // if (value >= 0) {
                          if (value === "" || /^[0-9]+$/.test(value)) {
                            handleChange("ratioQuantity")(e);
                          }
                        }}
                        inputProps={{ min: 0 }}
                        sx={{ width: "200px" }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {t("admin.manpowerRequirement.unitQuantityTooltip")}
                      </Typography>
                    </Box>

                    {/* 💡 Hint Message */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 0, fontStyle: "italic" }}
                    >
                      💡{" "}
                      {t("admin.manpowerRequirement.ratioHint", {
                        defaultValue:
                          "Hint: If your data is automated, the ratios will usually be smaller.",
                      })}
                    </Typography>

                    <Box display="flex" flexDirection="column" alignItems="flex-start" mt={1}>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body1">
                          {t("admin.manpowerRequirement.ratioLabel")} 1 :
                        </Typography>
                        <TextField
                          label={t("admin.manpowerRequirement.value")}
                          type="number"
                          margin="dense"
                          size="small"
                          value={ratioNo}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value > 0) {
                              setRatioNo(value);
                            }
                          }}
                          inputProps={{ min: 0, style: { width: "100px" } }}
                        />
                      </Box>

                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                        {t("admin.manpowerRequirement.unitsPerEmployeeTooltip")}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {hasEmployeeRatio === "no" && (
                  <>
                    <Box mt={2} display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setShowMore((prev) => !prev)}
                      >
                        {showMore
                          ? t("admin.manpowerRequirement.hide")
                          : t("admin.manpowerRequirement.show")}
                      </Button>
                    </Box>

                    <Collapse in={showMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.unitDailyUtilizationHours")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.dailyUtilizationHrs || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 24) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 24)
                            ) {
                              handleChange("dailyUtilizationHrs")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 24 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.unitDailyUtilizationHoursTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={showMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.unitWeeklyUtilizationDays")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.weeklyUtilizationDays || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 7) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 7)
                            ) {
                              handleChange("weeklyUtilizationDays")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 7 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.unitWeeklyUtilizationDaysTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={showMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.empDailyWorkingHours")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.dailyEmpWorkingHrs || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 24) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 24)
                            ) {
                              handleChange("dailyEmpWorkingHrs")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 24 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.empDailyWorkingHoursTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={showMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.empWeeklyWorkingDays")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.empWorkingDaysPerWeek || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 7) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 7)
                            ) {
                              handleChange("empWorkingDaysPerWeek")(e);
                            }
                          }}
                          // sx={{ width: "200px" }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 7 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.empWeeklyWorkingDaysTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={showMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.empDayOffsPerYear")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.vacationDays || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0) {
                            if (value === "" || (/^[0-9]+$/.test(value) && Number(value) >= 0)) {
                              handleChange("vacationDays")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.empDayOffsPerYearTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>
                  </>
                )}

                {hasEmployeeRatio === "no" && !formData.requirement?.isMultipleRatios && (
                  <Box display="flex" alignItems="center" gap={1.5} mt={2}>
                    <IconButton
                      onClick={handleAddExtraRatio}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        backgroundColor: "rgba(0, 123, 131, 0.1)", // light teal fill
                        color: "#007b83",
                        border: "1px solid rgba(0, 123, 131, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 123, 131, 0.2)",
                          borderColor: "rgba(0, 123, 131, 0.4)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>

                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 500, cursor: "pointer" }}
                    >
                      {t("admin.manpowerRequirement.addRatioButton")}
                    </Typography>
                  </Box>
                )}

                {hasEmployeeRatio === "no" && formData.requirement?.isMultipleRatios && (
                  <>
                    {/* ⭐ Shift Question */}
                    <Box mt={2}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body2">
                          {t("admin.manpowerRequirement.isShifts")}
                        </Typography>

                        <FormControl>
                          <RadioGroup
                            row
                            value={formData.requirement?.isShifts ? "yes" : "no"}
                            onChange={(e) => {
                              const booleanValue = e.target.value === "yes"; // convert string to boolean
                              handleChange("isShifts")({ target: { value: booleanValue } }); // pass a new object
                            }}
                          >
                            <FormControlLabel
                              value="yes"
                              control={
                                <Radio
                                  size="small"
                                  icon={
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: "2px solid #1976d2",
                                        display: "inline-block",
                                      }}
                                    />
                                  }
                                  checkedIcon={
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: "2px solid #1976d2",
                                        display: "inline-block",
                                        backgroundColor: "#1976d2",
                                      }}
                                    />
                                  }
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {t("admin.manpowerRequirement.ratioYes")}
                                </Typography>
                              }
                            />

                            <FormControlLabel
                              value="no"
                              control={
                                <Radio
                                  size="small"
                                  icon={
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: "2px solid #1976d2",
                                        display: "inline-block",
                                      }}
                                    />
                                  }
                                  checkedIcon={
                                    <span
                                      style={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: "50%",
                                        border: "2px solid #1976d2",
                                        display: "inline-block",
                                        backgroundColor: "#1976d2",
                                      }}
                                    />
                                  }
                                />
                              }
                              label={
                                <Typography variant="caption">
                                  {t("admin.manpowerRequirement.ratioNo")}
                                </Typography>
                              }
                            />
                          </RadioGroup>
                        </FormControl>
                      </Box>
                    </Box>
                  </>
                )}

                {hasEmployeeRatio === "no" && formData.requirement?.isMultipleRatios && (
                  <Box display="flex" flexDirection="column" gap={1} mt={2}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        label={t("admin.manpowerRequirement.name")}
                        margin="dense"
                        size="small"
                        value={
                          formData.requirement?.alternativeRatioName === "x*;_q*n!!e&m%p"
                            ? ""
                            : formData.requirement?.alternativeRatioName || ""
                        }
                        onChange={(e) => handleChange("alternativeRatioName")(e)}
                        inputProps={{ min: 0 }}
                        sx={{ width: "200px" }}
                      />

                      <Typography variant="caption" color="textSecondary">
                        {t("admin.manpowerRequirement.unitNameTooltip")}
                      </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        label={t("admin.manpowerRequirement.quantity")}
                        type="number"
                        margin="dense"
                        size="small"
                        value={formData.requirement?.alternativeRatioQuantity || ""}
                        onChange={(e) => {
                          const value = Number(e.target.value);
                          // if (value >= 0) {
                          if (value === "" || /^[0-9]+$/.test(value)) {
                            handleChange("alternativeRatioQuantity")(e);
                          }
                        }}
                        inputProps={{ min: 0 }}
                        sx={{ width: "200px" }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        {t("admin.manpowerRequirement.unitQuantityTooltip")}
                      </Typography>
                    </Box>

                    {/* 💡 Hint Message */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{ mt: 0, fontStyle: "italic" }}
                    >
                      💡{" "}
                      {t("admin.manpowerRequirement.ratioHint", {
                        defaultValue:
                          "Hint: If your data is automated, the ratios will usually be smaller.",
                      })}
                    </Typography>

                    <Box display="flex" flexDirection="column" alignItems="flex-start" mt={1}>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body1">
                          {t("admin.manpowerRequirement.ratioLabel")} 1 :
                        </Typography>
                        <TextField
                          label={t("admin.manpowerRequirement.value")}
                          type="number"
                          margin="dense"
                          size="small"
                          value={formData.requirement?.alternativeRatioValue}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            if (value > 0) {
                              // setRatioNo(value);
                              handleChange("alternativeRatioValue")(e);
                            }
                          }}
                          inputProps={{ min: 0, style: { width: "100px" } }}
                        />
                      </Box>

                      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                        {t("admin.manpowerRequirement.unitsPerEmployeeTooltip")}
                      </Typography>
                    </Box>
                  </Box>
                )}

                {hasEmployeeRatio === "no" && formData.requirement?.isMultipleRatios && (
                  <>
                    <Box mt={2} display="flex" justifyContent="flex-start">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => setAltShowMore((prev) => !prev)}
                      >
                        {altShowMore
                          ? t("admin.manpowerRequirement.hide")
                          : t("admin.manpowerRequirement.show")}
                      </Button>
                    </Box>

                    <Collapse in={altShowMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.unitDailyUtilizationHours")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.altDailyUtilizationHrs || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 24) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 24)
                            ) {
                              handleChange("altDailyUtilizationHrs")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 24 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.unitDailyUtilizationHoursTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={altShowMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.unitWeeklyUtilizationDays")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.altWeeklyUtilizationDays || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 7) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 7)
                            ) {
                              handleChange("altWeeklyUtilizationDays")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 7 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.unitWeeklyUtilizationDaysTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={altShowMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.empDailyWorkingHours")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.altDailyEmpWorkingHrs || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 24) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 24)
                            ) {
                              handleChange("altDailyEmpWorkingHrs")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 24 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.empDailyWorkingHoursTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={altShowMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.empWeeklyWorkingDays")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.altEmpWorkingDaysPerWeek || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0 && value <= 7) {
                            if (
                              value === "" ||
                              (/^[0-9]+$/.test(value) && Number(value) >= 0 && Number(value) <= 7)
                            ) {
                              handleChange("altEmpWorkingDaysPerWeek")(e);
                            }
                          }}
                          // sx={{ width: "200px" }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0, max: 7 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.empWeeklyWorkingDaysTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>

                    <Collapse in={altShowMore}>
                      <Box mt={1} display="flex" alignItems="center" gap={2}>
                        <TextField
                          label={t("admin.manpowerRequirement.empDayOffsPerYear")}
                          type="number"
                          margin="dense"
                          size="small"
                          fullWidth
                          value={formData.requirement?.altVacationDays || ""}
                          onChange={(e) => {
                            const value = Number(e.target.value);
                            // if (value >= 0) {
                            if (value === "" || (/^[0-9]+$/.test(value) && Number(value) >= 0)) {
                              handleChange("altVacationDays")(e);
                            }
                          }}
                          sx={{ maxWidth: "300px" }}
                          inputProps={{ min: 0 }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {t("admin.manpowerRequirement.empDayOffsPerYearTooltip")}
                        </Typography>
                      </Box>
                    </Collapse>
                  </>
                )}
              </Box>
            ) : (
              <Box mt={2} display="flex" flexDirection="column" gap={2}>
                {matchingRevGenerators.map((entry, index) => (
                  <RevenueGeneratorFields
                    key={entry.id}
                    id={entry.id}
                    // index={index}
                    handleSave={handleSaveFromChild}
                    handleUpdate={handleUpdateFromChild}
                    handleDelete={handleDeleteRevenueGenerator}
                    formData={entry} // formData is the single entry for rev generators (one rev generator) for selected job
                    productivityInputs={productivityInputs[entry.id]} // contains raw+period
                    productivityOptions={productivityOptions}
                    handleChangeRev={handleChangeRev}
                    handleProductivityChange={handleProductivityChange}
                    revLoading={revLoading}
                    revUpdateLoading={revUpdateLoading}
                    t={t}
                  />
                ))}

                {!hasPendingNew && (
                  <Box display="flex" alignItems="center" gap={1.5} mt={1}>
                    <IconButton
                      onClick={handleAddRevenueGenerator}
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: 2,
                        backgroundColor: "rgba(0, 123, 131, 0.1)", // light teal fill
                        color: "#007b83",
                        border: "1px solid rgba(0, 123, 131, 0.3)",
                        "&:hover": {
                          backgroundColor: "rgba(0, 123, 131, 0.2)",
                          borderColor: "rgba(0, 123, 131, 0.4)",
                        },
                        transition: "all 0.2s ease",
                      }}
                    >
                      <AddIcon sx={{ fontSize: 16 }} />
                    </IconButton>

                    <Typography
                      variant="body2"
                      sx={{ color: "black", fontWeight: 500, cursor: "default" }}
                    >
                      {t("admin.manpowerRequirement.addButton")}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}

            {/* --- Part 3: Workload Analysis --- */}
            <Box mt={3}>
              <Typography variant="subtitle2" gutterBottom>
                {t("admin.manpowerRequirement.workloadAnalysis")}
              </Typography>

              <Grid container spacing={2}>
                {/* Contractual Hours */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense">
                    <Tooltip
                      title={t("admin.manpowerRequirement.contractualHoursTooltip")}
                      placement="top-start"
                      arrow
                    >
                      <TextField
                        label={t("admin.manpowerRequirement.contractualHours")}
                        type="number"
                        size="small"
                        value={formData.requirement?.contractualHoursPerWeek || ""}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (value > 0) {
                            handleChange("contractualHoursPerWeek")(e);
                          }
                        }}
                        inputProps={{ min: 1 }}
                        sx={{ maxWidth: 200 }}
                      />
                    </Tooltip>
                  </FormControl>
                </Grid>

                {/* Actual Hours */}
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="dense">
                    <Box display="flex" alignItems="center" gap={2}>
                      <Tooltip
                        title={t("admin.manpowerRequirement.actualHoursTooltip")}
                        placement="top-start"
                        arrow
                      >
                        <TextField
                          label={t("admin.manpowerRequirement.actualHours")}
                          type="number"
                          size="small"
                          value={formData.requirement?.actualHoursPerWeek || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value >= 0) {
                              handleChange("actualHoursPerWeek")(e);
                            }
                          }}
                          inputProps={{ min: 0 }}
                          sx={{ maxWidth: 200 }}
                        />
                      </Tooltip>

                      <Tooltip title={t("admin.manpowerRequirement.hrsCalculator")}>
                        <Button
                          onClick={() => {
                            if (isScenario) {
                              navigate("/hours-calculator", {
                                state: {
                                  job,
                                  allJobs,
                                  // revenueGenerator: formData.revenueGenerator,
                                  formData: formData?.requirement,
                                  isScenario,
                                  ratio: hasEmployeeRatio === "yes" ? ratioYes : ratioNo,
                                  ratioSource: hasEmployeeRatio,
                                  loc,
                                  scenario,
                                },
                              });
                            } else {
                              // IF NOT SCENARIO: Navigate to Employee Workload
                              navigate("/employee-workload", {
                                state: {
                                  employeeWorkloads,
                                  contractualHours: job.contractualHours,
                                },
                              });
                            }
                          }}
                          sx={{ minWidth: "auto", padding: "4px" }}
                        >
                          {/* <span role="img" aria-label="calculator">🧮</span> */}
                          <CalculateIcon fontSize="large" />
                          {/* Or use MUI icon: <CalculateIcon fontSize="small" /> */}
                        </Button>
                      </Tooltip>
                    </Box>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>

          <DialogActions>
            <Button onClick={(e) => handleClose(e, "backdropClick")} color="secondary">
              {t("user.profile.cancel")}
            </Button>

            {loading ? (
              <Box display="flex" justifyContent="flex-end" p={2} flex={1}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Button
                onClick={async () => {
                  await handleSave();
                  (e) => handleClose(e, "backdropClick");
                }} // open confirmation dialog
                color="primary"
                sx={{ "&:hover": { backgroundColor: "blue", color: "#fff" } }}
              >
                {t("user.profile.save")}
              </Button>
            )}
          </DialogActions>
        </>
      )}

      <SnackbarWrapper
        ref={snackbarRef}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      />
    </Dialog>
  );
};

ManpowerReqDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onChangeRev: PropTypes.func.isRequired,
  manpowerReq: PropTypes.object,
  jobTitle: PropTypes.string,
  job: PropTypes.object,
  allJobs: PropTypes.array,
  matchingRevenueGenerator: PropTypes.object,
  isScenario: PropTypes.bool,
  loc: PropTypes.string,
  scenario: PropTypes.object,
};

export default ManpowerReqDialog;
